import { useEffect, useMemo } from 'react'
import { Grid, Image } from 'semantic-ui-react'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import {
  FETCH_USER_YEAR_END_MODULE_STATUSES_KEY,
  fetchUserYearEndModuleStatuses,
  FormFilingJobSubstep,
  YearEndModuleStatusOptions,
  YearEndModuleType,
} from '@/features/year-end-module-status/yearEndModuleStatus.slice'
import { useReselector } from '@/utils/sharedHooks'
import { getIsFetchingOrNotStarted } from '@/reducers/fetch'
import { Loader } from '@/ui'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { Accordion, Button, Icon, Text } from '@/componentsV1/BaseComponents'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { makeGridConfig } from '@/componentsV1/BaseComponents/Grid'
import YearEndModuleProgressList from '@/features/year-end-module-status/components/YearEndModuleProgressList'
import {
  getMostUrgentYearEndModuleType,
  selectOrderedCompletedYEModules,
  selectOrderedIncompleteYEModules,
  selectYearEndModuleByType,
  selectAllYearEndModulesComplete,
} from '@/features/year-end-module-status/yearEndModuleStatus.selectors'
import { getYearEndModuleStatuses } from '@/features/year-end-module-status/baseYearEndModuleStatus.selector'
import { fetchUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'
import { useYearEndNextStepPill } from '@/features/year-end-module-status/components/EOYNextStepPill'
import { getAnnualTaxFilingForYearSelector } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import { fetchAnnualTaxFilingFormsIfNeeded } from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import {
  fetchAnnualTaxFilingsIfNeeded,
  needContractorFilingOption,
} from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import {
  FAQsSection,
  GetTaxAdvice,
  OptInOrOutWithHeard,
  OptInto1099NEC,
  OptIntoExtension,
  OptOutOf1099NEC,
} from './RightSidebarItems'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'

import { SmsConsentButton } from '@/features/user/settings/sections/account-details-card/sms-consent/SmsConsentButton'
import OneFormExtensionNotice from './OneFormExtensionNotice'
import TaxConsultationCSATSurvey from '@/features/taxes/advisory/TaxConsultationSurvey'
import { TaxfylePortalError } from '@/features/taxes/annual-taxes/components/TaxfylePortalError'
import CompletedAllModulesCard from '../CompletedAllModulesCard'
import { useYearEndTaxfylePortal } from '@/features/year-end-module-status/yearEndModuleStatus.helpers'
import { QTEReminderBanner } from '@/features/taxes/quarterly/QTEReminderBanner'

const getHeaderContent = ({
  taxYear,
  optedOutOfTaxesWithHeard,
  completedAllModules,
}: {
  taxYear: string
  optedOutOfTaxesWithHeard: boolean
  completedAllModules: boolean
}) => {
  if (optedOutOfTaxesWithHeard && completedAllModules) {
    return {
      title: `${taxYear} Year-End Bookkeeping`,
      description: `You are all set with your ${taxYear} bookkeeping.`,
      image:
        'https://heard-images.s3.amazonaws.com/assets/books_lightShadow.svg',
    }
  }

  if (!optedOutOfTaxesWithHeard && completedAllModules) {
    return {
      title: 'Annual Taxes',
      description: `You finished your taxes for ${taxYear}! See below for a copy of your returns.`,
      image: 'https://heard-images.s3.amazonaws.com/assets/annual_taxes.svg',
    }
  }

  if (!optedOutOfTaxesWithHeard && !completedAllModules) {
    return {
      title: 'Annual Taxes',
      description:
        'We’ll walk you through getting your books and documents in order for annual taxes. After we have everything we need, we’ll connect you to a tax preparer.',
      image: 'https://heard-images.s3.amazonaws.com/assets/annual_taxes.svg',
    }
  }

  if (optedOutOfTaxesWithHeard && !completedAllModules) {
    return {
      title: `${taxYear} Year-End Bookkeeping`,
      description:
        'We’ll walk you through getting your books in order for the year. This will set you up for success when you file your annual taxes.',
      image:
        'https://heard-images.s3.amazonaws.com/assets/books_lightShadow.svg',
    }
  }

  return {
    title: 'Year-End Bookkeeping',
    description:
      'We’ll walk you through getting your books in order for the year. This will set you up for success when you file your annual taxes.',
    image: 'https://heard-images.s3.amazonaws.com/assets/books_lightShadow.svg',
  }
}

const Header = ({
  taxYear,
  optedOutOfTaxesWithHeard,
  completedAllModules,
}: {
  taxYear: string
  optedOutOfTaxesWithHeard: boolean
  completedAllModules: boolean
}) => {
  const nextStepPill = useYearEndNextStepPill()

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const content = getHeaderContent({
    taxYear,
    optedOutOfTaxesWithHeard,
    completedAllModules,
  })

  return (
    <Grid.Row>
      <Grid.Column
        width={13}
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <Text as="h1" style={{ marginBottom: 8 }}>
          {content.title}
        </Text>

        {!isMobile && (
          <Text as="bodySm" style={{ marginBottom: 24 }}>
            {content.description}
          </Text>
        )}
        {!completedAllModules && (
          <div style={{ display: 'inline-block' }}>{nextStepPill}</div>
        )}
      </Grid.Column>
      {!isMobile && (
        <Grid.Column
          width={3}
          textAlign="right"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 16,
          }}
        >
          <SmsConsentButton />
          <Image src={content.image} style={{ width: 180, height: 180 }} />
        </Grid.Column>
      )}
    </Grid.Row>
  )
}

const OptionsSection = ({
  taxYear,
  optedOutOfTaxesWithHeard,
  optedOutOf1099NEC,
}: {
  taxYear: string
  optedOutOfTaxesWithHeard: boolean
  optedOutOf1099NEC: boolean
}) => {
  const optionsTitle = <Text as="h2">Options</Text>
  const titleStyle = {
    display: 'flex',
    justifyContent: 'space-between',
  }

  const optInFlagOn = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enable2024OptOutFlow,
    false
  )

  const enableTaxConsultations = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableTaxConsultations,
    false
  )

  const optionsContent = useMemo(() => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <OptIntoExtension taxYear={taxYear} />
        {enableTaxConsultations && <GetTaxAdvice />}
        {optInFlagOn && (
          <OptInOrOutWithHeard
            taxYear={taxYear}
            optedOutOfTaxesWithHeard={optedOutOfTaxesWithHeard}
          />
        )}
        {optedOutOf1099NEC && <OptInto1099NEC taxYear={taxYear} />}
        <OptOutOf1099NEC />
      </div>
    )
  }, [
    taxYear,
    optInFlagOn,
    optedOutOfTaxesWithHeard,
    optedOutOf1099NEC,
    enableTaxConsultations,
  ])

  return (
    <Accordion
      title={optionsTitle}
      titleStyle={titleStyle}
      content={optionsContent}
      variant="text"
      iconColor="black"
      initialOpen
    />
  )
}

const useTaxFyleLinkIfComplete = ({
  moduleType,
  linkText,
}: {
  moduleType: YearEndModuleType
  linkText: string
}) => {
  const module = useReselector(selectYearEndModuleByType, moduleType)
  const portal = useYearEndTaxfylePortal({ moduleType, status: module?.status })

  if (
    !portal.available ||
    (module?.status !== YearEndModuleStatusOptions.complete &&
      module?.substepIdentifier !== FormFilingJobSubstep.updatePaymentInfo)
  ) {
    return null
  }

  return (
    <div>
      <Button
        variant="link"
        loading={portal.loading}
        onClick={() => {
          void portal.open()
        }}
        style={{ marginTop: 10, width: '100%', justifyContent: 'flex-start' }}
      >
        {linkText}
        <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />
      </Button>
      <TaxfylePortalError error={portal.error} />
    </div>
  )
}

const CompletedModulesMessages = () => {
  const file1040Link = useTaxFyleLinkIfComplete({
    moduleType: YearEndModuleType.file1040,
    linkText: 'View Personal Tax Return chat',
  })

  const file1120Link = useTaxFyleLinkIfComplete({
    moduleType: YearEndModuleType.file1120S,
    linkText: 'View Business Tax Return chat',
  })

  const fileExtensionLink = useTaxFyleLinkIfComplete({
    moduleType: YearEndModuleType.fileExtensionRequest,
    linkText: 'View Extension chat',
  })

  if (!file1040Link && !file1120Link && !fileExtensionLink) {
    return null
  }

  return (
    <Accordion
      title={<Text as="h2">Tax Preparation</Text>}
      content={
        <>
          <Text as="bodySm">
            Access your conversations with your tax preparer at any time.
          </Text>
          {file1120Link}
          {file1040Link}
          {fileExtensionLink}
        </>
      }
      variant="text"
      iconColor="black"
      initialOpen
      titleStyle={{
        display: 'flex',
        justifyContent: 'space-between',
      }}
    />
  )
}

const RightSideContent = (props: {
  taxYear: string
  optedOutOfTaxesWithHeard: boolean
  optedOutOf1099NEC: boolean
  completedAllModules: boolean
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
      <CompletedModulesMessages />
      <OneFormExtensionNotice />
      <FAQsSection />
      {!props.completedAllModules && <OptionsSection {...props} />}
    </div>
  )
}

const YearEndHome = () => {
  const dispatch = useAppDispatch()
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const completedYEModules = useReselector(selectOrderedCompletedYEModules)
  const incompleteModules = useReselector(selectOrderedIncompleteYEModules)
  const mostUrgentModuleType = useReselector(getMostUrgentYearEndModuleType)
  const completedAllModules = useReselector(selectAllYearEndModulesComplete)
  const modules = useReselector(getYearEndModuleStatuses)
  const fetchingModules = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_USER_YEAR_END_MODULE_STATUSES_KEY
  )

  const {
    optedOutAt,
    needContractorFiling,
    extensionReason,
    annualTaxFormNeeds,
  } = useReselector(getAnnualTaxFilingForYearSelector, taxYear) || {}
  const needs1099NEC = needContractorFiling === needContractorFilingOption.yes

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const gridStyle = isMobile
    ? { padding: '0px 16px' }
    : { padding: '0px 20px 0px 0px' }
  useEffect(() => {
    dispatch(fetchUserYearEndModuleStatuses())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
    dispatch(fetchAnnualTaxFilingsIfNeeded())
    dispatch(fetchAnnualTaxFilingFormsIfNeeded())
  }, [dispatch, taxYear])

  // Only before the first load: a refetch keeps the cards already on screen.
  if (fetchingModules && modules.length === 0) {
    return <Loader role="status" aria-label="Loading" />
  }

  return (
    <>
      <QTEReminderBanner />
      <Grid style={gridStyle}>
        <Header
          taxYear={taxYear}
          optedOutOfTaxesWithHeard={Boolean(optedOutAt)}
          completedAllModules={completedAllModules}
        />
        <Grid.Row>
          <Grid.Column {...makeGridConfig([11, 16])}>
            {completedAllModules && (
              <Grid.Row>
                <CompletedAllModulesCard
                  taxYear={taxYear}
                  optedOutOfTaxesWithHeard={Boolean(optedOutAt)}
                />
              </Grid.Row>
            )}
            <YearEndModuleProgressList
              completeModules={completedYEModules}
              incompleteModules={incompleteModules}
              taxYear={taxYear}
              mostUrgentModule={mostUrgentModuleType}
              annualTaxFormNeeds={annualTaxFormNeeds}
              extensionReason={extensionReason}
            />
          </Grid.Column>
          <Grid.Column {...makeGridConfig([5, 16])}>
            <RightSideContent
              taxYear={taxYear}
              optedOutOfTaxesWithHeard={Boolean(optedOutAt)}
              optedOutOf1099NEC={!needs1099NEC}
              completedAllModules={completedAllModules}
            />
          </Grid.Column>
        </Grid.Row>
        <TaxConsultationCSATSurvey />
      </Grid>
    </>
  )
}

export default YearEndHome
